/**
 * MaterialGroupedPicker — 2-step cascaded material selection.
 *
 * Step 1: Search and pick a BASE ITEM (e.g. "TMT Bar")
 * Step 2: Filter/pick a VARIANT by grade → size → brand (any order)
 *
 * The result is a full material_master record (same shape as MaterialPicker).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronRight, Package, Tag, Ruler, Building2, Loader2 } from 'lucide-react';
import { apiGet } from '../../lib/api';

export const MaterialGroupedPicker = ({
  value,
  displayName,
  onSelect,
  placeholder = 'Search material…',
  testid = 'material-grouped-picker',
  compact = false,
  disabled = false,
}) => {
  const [step, setStep] = useState(1);          // 1 = pick base item, 2 = pick variant
  const [q, setQ] = useState('');
  const [baseItems, setBaseItems] = useState([]);
  const [selectedBase, setSelectedBase] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loadingBase, setLoadingBase] = useState(false);
  const [loadingVar, setLoadingVar] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value ? { id: value, name: displayName } : null);
  // Variant filters
  const [fGrade, setFGrade] = useState('');
  const [fSize, setFSize] = useState('');
  const [fBrand, setFBrand] = useState('');

  const boxRef = useRef(null);
  const triggerRef = useRef(null);
  const debRef = useRef(null);
  const abortRef = useRef(null);  // AbortController ref — cancels stale in-flight requests
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

  const openDropdown = () => {
    if (disabled) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 360),
      });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!value) setSelected(null);
    else setSelected(p => (p?.id === value ? p : { id: value, name: displayName }));
  }, [value, displayName]);

  useEffect(() => {
    const onDoc = e => {
      // Close if click is outside the trigger AND outside the portal dropdown
      const isInsideTrigger = boxRef.current && boxRef.current.contains(e.target);
      const isInsideDropdown = e.target.closest('[data-testid$="-dropdown"]');
      if (!isInsideTrigger && !isInsideDropdown) {
        setOpen(false);
        resetState();
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const resetState = () => {
    setStep(1); setQ(''); setBaseItems([]); setSelectedBase(null);
    setVariants([]); setFGrade(''); setFSize(''); setFBrand('');
  };

  // Search base items (debounced + AbortController to discard stale responses)
  useEffect(() => {
    if (!open || step !== 1) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      // Cancel any previous in-flight request before starting a new one
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      const { signal } = abortRef.current;
      setLoadingBase(true);
      try {
        const d = await apiGet('/material-master/base-items', { q: q || undefined, limit: 30 });
        if (!signal.aborted) setBaseItems(d.items || []);
      } catch (err) {
        if (!signal.aborted) setBaseItems([]);
      } finally {
        if (!signal.aborted) setLoadingBase(false);
      }
    }, 250);
  }, [q, open, step]);

  // Load variants when base item selected — skip step 2 if only one variant
  const pickBase = async (item) => {
    setSelectedBase(item);
    setLoadingVar(true);
    try {
      const d = await apiGet(`/material-master/base-items/${encodeURIComponent(item.base_item_name)}/variants`);
      const vars = d.variants || [];
      setVariants(vars);
      // Fix 1E: single-variant base items select directly without step 2
      if (vars.length === 1) {
        onSelect(vars[0]);
        setOpen(false);
        return;
      }
    } catch { setVariants([]); }
    finally { setLoadingVar(false); }
    setStep(2);
  };

  // Derived variant filter options
  const grades  = [...new Set(variants.map(v => v.grade).filter(Boolean))].sort();
  const sizes   = [...new Set(variants
    .filter(v => !fGrade || v.grade === fGrade)
    .map(v => v.size).filter(Boolean))].sort();
  const brands  = [...new Set(variants
    .filter(v => (!fGrade || v.grade === fGrade) && (!fSize || v.size === fSize))
    .map(v => v.brand).filter(Boolean))].sort();

  const filteredVariants = variants.filter(v =>
    (!fGrade || v.grade === fGrade) &&
    (!fSize  || v.size  === fSize)  &&
    (!fBrand || (v.brand || '').toLowerCase().includes(fBrand.toLowerCase()))
  );

  const pickVariant = (mat) => {
    setSelected(mat);
    onSelect?.(mat);
    setOpen(false);
    resetState();
  };

  const clear = e => {
    e.stopPropagation();
    setSelected(null);
    onSelect?.(null);
  };

  const displayValue = selected?.name || displayName || '';

  return (
    <div ref={boxRef} className={`relative ${compact ? '' : 'w-full'}`} data-testid={testid}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={openDropdown}
        className={`flex items-center gap-2 border rounded-sm bg-white cursor-pointer transition-colors
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#3b4872]'}
          ${open ? 'border-[#3b4872] ring-1 ring-[#3b4872]/20' : 'border-slate-300'}
          ${compact ? 'px-2 py-1.5' : 'px-3 py-2'}`}
        data-testid={`${testid}-trigger`}
      >
        <Package size={compact ? 12 : 14} className="text-slate-400 shrink-0" />
        <span className={`flex-1 truncate ${compact ? 'text-xs' : 'text-sm'} ${displayValue ? 'text-slate-800' : 'text-slate-400'}`}
          title={displayValue || ''}>
          {displayValue || placeholder}
        </span>
        {selected && !disabled && (
          <button onClick={clear} className="text-slate-300 hover:text-slate-600" data-testid={`${testid}-clear`}>
            <X size={12} />
          </button>
        )}
      </div>

      {/* Dropdown — rendered via portal to escape any overflow:hidden/auto parent */}
      {open && createPortal(
        <div
          className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden"
          style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width, maxHeight: 440 }}
          data-testid={`${testid}-dropdown`}
        >

          {/* Step 1 — pick base item */}
          {step === 1 && (
            <>
              <div className="px-3 pt-2.5 pb-2 border-b border-slate-100">
                <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Step 1 — Select Item</div>
                <div className="flex items-center gap-2 border border-slate-200 rounded-sm px-2 py-1.5 focus-within:border-[#3b4872]">
                  <Search size={12} className="text-slate-400 shrink-0" />
                  <input
                    autoFocus
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="Type to search items…"
                    className="flex-1 text-sm outline-none bg-transparent"
                    data-testid={`${testid}-step1-search`}
                  />
                  {loadingBase && <Loader2 size={12} className="animate-spin text-slate-400" />}
                </div>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 340 }}>
                {baseItems.length === 0 && !loadingBase && (
                  <p className="py-6 text-center text-sm text-slate-400">
                    {q ? 'No items found' : 'Start typing to search…'}
                  </p>
                )}
                {baseItems.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => pickBase(item)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-slate-50 border-b border-slate-50 transition-colors"
                    data-testid={`${testid}-base-${i}`}
                  >
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-[13px] text-slate-800 truncate">
                        {item.base_item_name}
                      </div>
                      <div className="text-[10px] text-slate-400 truncate">
                        {item.sub_category} · {item.uom} · HSN {item.hsn_code}
                        {item.grades?.length > 0 && ` · ${item.grades.slice(0,3).join(', ')}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      <span className="text-[10px] bg-[#3b4872]/10 text-[#3b4872] font-bold px-1.5 py-0.5 rounded">
                        {item.variant_count} var.
                      </span>
                      <ChevronRight size={14} className="text-slate-300" />
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step 2 — pick variant */}
          {step === 2 && (
            <>
              <div className="px-3 pt-2.5 pb-2 border-b border-slate-100">
                <button
                  onClick={() => { setStep(1); setSelectedBase(null); setFGrade(''); setFSize(''); setFBrand(''); }}
                  className="flex items-center gap-1.5 text-[10px] text-[#3b4872] font-bold hover:underline mb-1.5"
                  data-testid={`${testid}-back`}
                >
                  ← {selectedBase?.base_item_name}
                </button>
                <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Step 2 — Select Variant</div>
                {/* Filter chips */}
                <div className="flex flex-wrap gap-1.5">
                  {grades.length > 1 && (
                    <div className="flex items-center gap-1">
                      <Tag size={9} className="text-slate-400" />
                      <select value={fGrade} onChange={e => { setFGrade(e.target.value); setFSize(''); setFBrand(''); }}
                        className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:border-[#3b4872]"
                        data-testid={`${testid}-filter-grade`}
                      >
                        <option value="">All grades</option>
                        {grades.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                  )}
                  {sizes.length > 1 && (
                    <div className="flex items-center gap-1">
                      <Ruler size={9} className="text-slate-400" />
                      <select value={fSize} onChange={e => { setFSize(e.target.value); setFBrand(''); }}
                        className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:border-[#3b4872]"
                        data-testid={`${testid}-filter-size`}
                      >
                        <option value="">All sizes</option>
                        {sizes.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  {brands.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Building2 size={9} className="text-slate-400" />
                      <input
                        list={`brand-list-${selectedBase?.base_item_name}`}
                        value={fBrand}
                        onChange={e => setFBrand(e.target.value)}
                        placeholder="Brand…"
                        className="text-[11px] border border-slate-200 rounded-sm px-2 py-0.5 focus:outline-none focus:border-[#3b4872] w-28"
                        data-testid={`${testid}-filter-brand`}
                      />
                      <datalist id={`brand-list-${selectedBase?.base_item_name}`}>
                        {brands.map(b => <option key={b} value={b} />)}
                      </datalist>
                    </div>
                  )}
                </div>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 300 }}>
                {loadingVar && <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-slate-400" /></div>}
                {!loadingVar && filteredVariants.length === 0 && (
                  <p className="py-6 text-center text-sm text-slate-400">No variants match the filters</p>
                )}
                {filteredVariants.map(mat => (
                  <button
                    key={mat.id}
                    onClick={() => pickVariant(mat)}
                    className="w-full text-left px-3 py-2 hover:bg-[#3b4872]/5 border-b border-slate-50 transition-colors"
                    data-testid={`${testid}-variant-${mat.material_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-[13px] text-slate-800 truncate">{mat.name}</div>
                        <div className="text-[10px] text-slate-400 flex flex-wrap gap-2 mt-0.5">
                          {mat.grade && <span className="flex items-center gap-0.5"><Tag size={8}/>{mat.grade}</span>}
                          {mat.size  && <span className="flex items-center gap-0.5"><Ruler size={8}/>{mat.size}</span>}
                          {mat.brand && <span className="flex items-center gap-0.5"><Building2 size={8}/>{mat.brand}</span>}
                          {mat.specification && <span className="truncate max-w-[180px]">{mat.specification.slice(0,60)}</span>}
                        </div>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono shrink-0">{mat.material_id}</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default MaterialGroupedPicker;
